/**
 * AmourLinen Persona Result (v8.2 — tote photo swap)
 * - All customer-facing strings come from the per-locale JSON config
 *   rendered by sections/persona-result.liquid
 * - Tier columns: locked = blurred, unlocked = clear (all blurred at 0 items)
 * - Single dynamic status line under tiers
 * - Unlock bar pinned below the site header, tracks it on scroll
 * - v7.1: scroll position locked for 2s around add-to-cart (guardScrollPosition)
 * - v8.0: section is now always visible (was display:none until a ?result=
 *   param existed); falls back to the saved quiz result in localStorage,
 *   then to the pre-quiz teaser card. "Find out" in the teaser dispatches
 *   amourlinen:start-quiz so the Style Quiz section above can pick it up.
 * - v8.1: on mobile (<=749px), the tier list labels are shortened purely at
 *   the DOM level — "2 pieces" -> "2pcs", "+ Linen tote bag" -> "+ tote
 *   bag" — to match the reference design's compact single-line layout,
 *   without touching the (very large) Liquid template that owns the full
 *   copy. Desktop keeps the full wording; switches back live via
 *   matchMedia if the viewport crosses the breakpoint.
 * - v8.2: swap the tote-card photo to the "Tote_Yellow" file uploaded in
 *   Shopify Files, done at the DOM level for the same reason as v8.1 —
 *   sections/persona-result.liquid is ~56,000 characters and Shopify's
 *   theme file API only accepts full-body rewrites, so a one-line change
 *   there means resending the entire file. Swapping the <img src> here
 *   is equivalent and far lower-risk.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'amourlinen_quiz_result';
  const SAVED_PRODUCTS_KEY = 'amourlinen_saved_products';
  const TOTE_IMAGE_URL = 'https://cdn.shopify.com/s/files/1/0266/3206/0972/files/Tote_Yellow.jpg?v=1785321345';

  function esc(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  class PersonaResult {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.personaBlocks = section.querySelectorAll('[data-persona]');
      this.noResultBlock = section.querySelector('[data-no-result]');
      this.barEl = section.querySelector('[data-bar]');
      this.activeBlock = null;

      this.config = this.loadConfig();
      this._lastBarThreshold = null;

      this.init();
    }

    loadConfig() {
      const fallback = {
        handles: [],
        toteThreshold: 3,
        barSubtitle: 'Discount applied at checkout',
        bestOfferSuffix: ', best offer unlocked',
        regularUnlockSuffix: ' unlocked',
        redPhrase: '+ tote bag',
        statusEmptyPre: 'Select any',
        statusEmptyStrong: '2 clothing pieces',
        statusEmptyPost: 'to unlock your first discount.',
        statusProgressLead: '[count] selected.',
        statusProgressRest: 'Add [needed] more to unlock [next].',
        wordItemOne: 'piece',
        wordItemMany: 'pieces',
        savedConfirm: 'Saved'
      };
      try {
        const el = this.section.querySelector('script[data-persona-result-config]');
        if (!el) return fallback;
        return Object.assign(fallback, JSON.parse(el.textContent));
      } catch (e) {
        return fallback;
      }
    }

    init() {
      const personaSlug = this.getPersonaSlug();

      this.section.style.display = '';
      this.showPersona(personaSlug);
      this.bindEvents();
      this.initCarousels();
      this.restoreSavedProducts();
      this.syncTiers();
      this.setupMobileTierLabels();
      this.updateToteImage();

      this.positionBar();
      window.addEventListener('scroll', () => this.positionBar(), { passive: true });
      document.addEventListener('mouseover', (e) => {
        if (e.target.closest('header, .header-wrapper, .shopify-section-group-header-group')) this.positionBar();
      });
      document.addEventListener('mouseout', (e) => {
        if (e.target.closest('header, .header-wrapper, .shopify-section-group-header-group')) {
          setTimeout(() => this.positionBar(), 150);
        }
      });
      window.addEventListener('resize', () => this.positionBar());
      window.addEventListener('focus', () => this.syncTiers());
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.syncTiers();
      });
    }

    /* v8.2: swap the tote-card photo (in both the actual persona-result
       cards and the pre-quiz no-result teaser, since they share the same
       markup) to the "Tote_Yellow" file uploaded in Shopify Files. Only
       touches images still pointing at the old filename, so it's safe to
       run even if the Liquid template is updated later. */
    updateToteImage() {
      this.section.querySelectorAll('.persona-result__tote-card-image img').forEach((img) => {
        if (/Totebag_main/i.test(img.src)) {
          img.src = TOTE_IMAGE_URL;
        }
      });
    }

    /* v8.1: shorten tier-pieces ("2 pieces" -> "2pcs") and tier-extra
       ("+ Linen tote bag" -> "+ tote bag") on mobile only, so the 4 tier
       columns fit on one line each and line up evenly, matching the
       compact reference design. Original text is cached on the element
       so it can be restored exactly if the viewport grows back past the
       breakpoint (e.g. rotating a tablet, or resizing a desktop window). */
    setupMobileTierLabels() {
      const mq = window.matchMedia('(max-width: 749px)');

      const apply = () => {
        const isMobile = mq.matches;

        this.section.querySelectorAll('.persona-result__tier-pieces').forEach((el) => {
          if (el.dataset.fullText === undefined) el.dataset.fullText = el.textContent;
          el.textContent = isMobile
            ? el.dataset.fullText.replace(/\s*pieces?\s*$/i, 'pcs')
            : el.dataset.fullText;
        });

        this.section.querySelectorAll('.persona-result__tier-extra').forEach((el) => {
          if (el.dataset.fullText === undefined) el.dataset.fullText = el.textContent;
          el.textContent = isMobile
            ? el.dataset.fullText.replace(/\bLinen\s+/i, '')
            : el.dataset.fullText;
        });
      };

      apply();
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', apply);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(apply);
      }
    }

    /* Prefer the explicit ?result= param (set when someone clicks through
       from the quiz or a "view your edit" link); fall back to whatever
       persona is already saved in localStorage so a returning visitor who
       scrolls straight past the quiz still sees their result instead of
       the pre-quiz teaser. */
    getPersonaSlug() {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('result');
      if (fromUrl) return fromUrl;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          return data.persona || null;
        }
      } catch (e) {}
      return null;
    }

    showPersona(personaSlug) {
      let matched = false;
      this.personaBlocks.forEach(block => {
        if (personaSlug && block.dataset.persona === personaSlug) {
          block.hidden = false;
          requestAnimationFrame(() => block.classList.add('is-visible'));
          matched = true;
          this.activeBlock = block;
        } else {
          block.hidden = true;
        }
      });
      if (!matched && this.noResultBlock) {
        this.noResultBlock.hidden = false;
        this.activeBlock = this.noResultBlock;
      }
    }

    bindEvents() {
      this.section.addEventListener('click', (e) => {
        if (e.target.closest('[data-scroll-to-products]')) { this.scrollToProducts(); return; }
        if (e.target.closest('[data-retake-quiz]')) { this.handleRetake(); return; }
        if (e.target.closest('[data-start-quiz]')) { e.preventDefault(); this.handleStartQuiz(); return; }
        const prev = e.target.closest('[data-carousel-prev]'); if (prev) { this.scrollCarousel(prev, 'prev'); return; }
        const next = e.target.closest('[data-carousel-next]'); if (next) { this.scrollCarousel(next, 'next'); return; }
        const saveCard = e.target.closest('[data-save-product]'); if (saveCard) { this.toggleSaveProduct(saveCard); return; }
        const qaToggle = e.target.closest('[data-quick-add-toggle]'); if (qaToggle) { this.toggleQuickAddPanel(qaToggle); return; }
        const size = e.target.closest('.persona-result__size-btn'); if (size) { this.selectSize(size); return; }
        const add = e.target.closest('[data-add-single]'); if (add) { this.handleAdd(add); return; }
        const saveR = e.target.closest('[data-save-results]'); if (saveR && !saveR.disabled) { this.confirmSaved(saveR); return; }
      });
    }

    scrollToProducts() {
      const target = this.activeBlock && this.activeBlock.querySelector('.persona-result__categories');
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    handleRetake() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      window.location.href = window.location.pathname;
    }

    /* Pre-quiz teaser CTA ("Find out" text + image): rather than reload,
       hand off to the Style Quiz section (which sits above this one on the
       page) so it can scroll into view and start itself. */
    handleStartQuiz() {
      document.dispatchEvent(new CustomEvent('amourlinen:start-quiz'));
    }

    initCarousels() {
      if (!this.activeBlock) return;
      this.activeBlock.querySelectorAll('[data-carousel]').forEach(carousel => {
        this.updateArrowStates(carousel);
        carousel.addEventListener('scroll', () => this.updateArrowStates(carousel));
      });
    }

    scrollCarousel(arrowBtn, direction) {
      const wrap = arrowBtn.closest('.persona-result__carousel-wrap');
      if (!wrap) return;
      const carousel = wrap.querySelector('[data-carousel]');
      if (!carousel) return;
      const card = carousel.querySelector('.persona-result__product');
      const cardWidth = card ? card.getBoundingClientRect().width : 320;
      carousel.scrollBy({ left: direction === 'next' ? cardWidth + 24 : -(cardWidth + 24), behavior: 'smooth' });
    }

    updateArrowStates(carousel) {
      const wrap = carousel.closest('.persona-result__carousel-wrap');
      if (!wrap) return;
      const prev = wrap.querySelector('[data-carousel-prev]');
      const next = wrap.querySelector('[data-carousel-next]');
      const atStart = carousel.scrollLeft <= 2;
      const atEnd = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    getSavedProducts() {
      try { const s = localStorage.getItem(SAVED_PRODUCTS_KEY); return s ? JSON.parse(s) : []; } catch (e) { return []; }
    }
    setSavedProducts(arr) {
      try { localStorage.setItem(SAVED_PRODUCTS_KEY, JSON.stringify(arr)); } catch (e) {}
    }

    restoreSavedProducts() {
      if (!this.activeBlock) return;
      const saved = this.getSavedProducts();
      this.activeBlock.querySelectorAll('[data-save-product]').forEach(btn => {
        if (saved.includes(btn.dataset.saveProduct)) btn.classList.add('is-saved');
      });
    }

    toggleSaveProduct(btn) {
      const id = btn.dataset.saveProduct;
      if (!id) return;
      let saved = this.getSavedProducts();
      if (saved.includes(id)) { saved = saved.filter(s => s !== id); btn.classList.remove('is-saved'); }
      else { saved.push(id); btn.classList.add('is-saved'); }
      this.setSavedProducts(saved);
    }

    toggleQuickAddPanel(btn) {
      const wrap = btn.closest('[data-quick-add-wrap]');
      if (!wrap) return;
      const panel = wrap.querySelector('[data-quick-add-panel]');
      if (!panel) return;
      if (this.activeBlock) {
        this.activeBlock.querySelectorAll('[data-quick-add-panel]').forEach(p => { if (p !== panel) p.hidden = true; });
      }
      panel.hidden = !panel.hidden;
    }

    selectSize(sizeBtn) {
      if (sizeBtn.disabled) return;
      const panel = sizeBtn.closest('[data-quick-add-panel]');
      if (!panel) return;
      panel.querySelectorAll('.persona-result__size-btn').forEach(b => b.classList.remove('is-selected'));
      sizeBtn.classList.add('is-selected');
    }

    getSelectedVariantId(card) {
      const panel = card.querySelector('[data-quick-add-panel]');
      if (!panel) return null;
      const selected = panel.querySelector('.persona-result__size-btn.is-selected');
      if (selected) return selected.dataset.variantId;
      const hidden = panel.querySelector('input[data-variant-id]');
      if (hidden) return hidden.value;
      return null;
    }

    guardScrollPosition(durationMs) {
      const y = window.scrollY;
      const snap = () => {
        if (Math.abs(window.scrollY - y) > 50) {
          window.scrollTo(0, y);
        }
      };
      window.addEventListener('scroll', snap, { passive: true });
      setTimeout(() => {
        window.removeEventListener('scroll', snap);
        snap();
      }, durationMs);
    }

    async handleAdd(btn) {
      const card = btn.closest('.persona-result__product');
      if (!card) return;
      const variantId = this.getSelectedVariantId(card);
      if (!variantId) {
        const panel = card.querySelector('[data-quick-add-panel]');
        const sizeRow = panel && panel.querySelector('.persona-result__size-row');
        if (sizeRow) {
          sizeRow.style.outline = '2px solid #B92825';
          sizeRow.style.outlineOffset = '2px';
          setTimeout(() => { sizeRow.style.outline = ''; }, 1800);
        }
        return;
      }

      this.guardScrollPosition(2000);

      btn.disabled = true;
      const def = btn.querySelector('[data-add-default]');
      const added = btn.querySelector('[data-add-added]');
      try {
        await this.addToCart([{ id: variantId, quantity: 1 }]);
        if (def) def.hidden = true;
        if (added) added.hidden = false;
        setTimeout(() => {
          btn.disabled = false;
          if (def) def.hidden = false;
          if (added) added.hidden = true;
          const panel = card.querySelector('[data-quick-add-panel]');
          if (panel) panel.hidden = true;
        }, 1800);
        await this.syncTiers();
        this.openCart();
      } catch (err) {
        console.error('Add to cart failed:', err);
        btn.disabled = false;
      }
    }

    async addToCart(items) {
      const cartDrawerEl = document.querySelector('cart-drawer');
      const sectionsToRender = this.getSectionsToRender(cartDrawerEl);
      const body = { items };
      if (sectionsToRender.length > 0) {
        body.sections = sectionsToRender.join(',');
        body.sections_url = window.location.pathname;
      }
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const eb = await response.json().catch(() => ({}));
        throw new Error(eb.description || 'Add to cart failed');
      }
      const data = await response.json();
      if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function' && data.sections) {
        try { cartDrawerEl.renderContents(data); } catch (e) { await this.manualCartDrawerRefresh(data); }
      } else if (data.sections) {
        await this.manualCartDrawerRefresh(data);
      } else {
        await this.fetchAndSwapCartDrawer();
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true, detail: { items } }));
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));
      return data;
    }

    getSectionsToRender(cartDrawerEl) {
      if (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function') {
        try {
          const s = cartDrawerEl.getSectionsToRender();
          const ids = s.map(x => x.id || x.section || (typeof x === 'string' ? x : null)).filter(Boolean);
          if (ids.length > 0) return ids;
        } catch (e) {}
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    async manualCartDrawerRefresh(data) {
      const parser = new DOMParser();
      if (data.sections && data.sections['cart-drawer']) {
        const el = document.querySelector('cart-drawer');
        if (el) {
          const doc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
          const fresh = doc.querySelector('cart-drawer');
          if (fresh) el.innerHTML = fresh.innerHTML;
        }
      }
      if (data.sections && data.sections['cart-icon-bubble']) {
        const el = document.querySelector('#cart-icon-bubble') || document.querySelector('[id*="cart-icon"]');
        if (el) {
          const doc = parser.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          const fresh = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
          if (fresh) el.innerHTML = fresh.innerHTML;
        }
      }
    }

    async fetchAndSwapCartDrawer() {
      try {
        const r = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        if (!r.ok) return;
        const html = await r.text();
        const el = document.querySelector('cart-drawer');
        if (!el) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        if (fresh) el.innerHTML = fresh.innerHTML;
      } catch (e) {}
    }

    openCart() {
      const d = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer');
      if (d && typeof d.open === 'function') { d.open(); return; }
      const t = document.querySelector('[data-cart-drawer-toggle], [aria-controls="CartDrawer"]');
      if (t) {
        const evt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
        t.dispatchEvent(evt);
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:open'));
    }

    confirmSaved(btn) { btn.textContent = this.config.savedConfirm || 'Saved'; btn.disabled = true; }

    // ============ UNLOCK BAR POSITION ============
    positionBar() {
      if (!this.barEl) return;
      let top = 0;
      const header = document.querySelector('sticky-header, header.header, .header-wrapper, .shopify-section-group-header-group header, .section-header header, header');
      if (header) {
        const r = header.getBoundingClientRect();
        if (r.bottom > 0 && r.bottom < window.innerHeight) top = Math.round(r.bottom);

        let z = NaN;
        let node = header;
        while (node && node !== document.body) {
          const v = parseInt(window.getComputedStyle(node).zIndex, 10);
          if (!isNaN(v)) { z = v; break; }
          node = node.parentElement;
        }
        this.barEl.style.zIndex = isNaN(z) ? 50 : Math.max(z - 1, 10);
      }
      this.barEl.style.top = top + 'px';
    }

    // ============ TIER SYNC ============
    async syncTiers() {
      if (!this.activeBlock) return;

      let cart;
      try {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        cart = await r.json();
      } catch (e) { return; }

      const eligibleHandles = new Set(this.config.handles || []);
      let eligibleCount = 0;
      if (cart && Array.isArray(cart.items)) {
        cart.items.forEach(item => {
          if (eligibleHandles.has(item.handle)) eligibleCount += item.quantity;
        });
      }

      const tierEls = Array.from(this.activeBlock.querySelectorAll('[data-tier-threshold]'));
      const tiers = tierEls
        .map(el => {
          const threshold = parseInt(el.dataset.tierThreshold, 10);
          const amountEl = el.querySelector('[data-tier-amount]');
          return {
            el: el,
            threshold: threshold,
            amount: amountEl ? amountEl.textContent.trim() : '',
            statusLead: el.dataset.statusLead || '',
            statusRest: el.dataset.statusRest || '',
            isTop: el.dataset.tierIsTop === 'true'
          };
        })
        .filter(t => !isNaN(t.threshold))
        .sort((a, b) => a.threshold - b.threshold);

      let best = null;
      tiers.forEach(t => {
        const unlocked = eligibleCount >= t.threshold;
        t.el.dataset.tierUnlocked = unlocked ? 'true' : 'false';
        if (unlocked) best = t;
      });

      this.updateStatus(eligibleCount, tiers, best);
      this.updateBar(best ? best.el : null);
      this.updateTote(eligibleCount);
    }

    wrapRed(escapedText) {
      const phrase = this.config.redPhrase ? esc(this.config.redPhrase) : '';
      if (!phrase) return escapedText;
      return escapedText.split(phrase).join('<span class="pr-red">' + phrase + '</span>');
    }

    itemWord(n) {
      return n === 1 ? (this.config.wordItemOne || 'piece') : (this.config.wordItemMany || 'pieces');
    }

    updateStatus(count, tiers, best) {
      const statusEl = this.activeBlock.querySelector('[data-status]');
      if (!statusEl) return;
      const c = this.config;
      let html = '';

      if (count === 0) {
        html = esc(c.statusEmptyPre) + ' <strong>' + esc(c.statusEmptyStrong) + '</strong> ' + esc(c.statusEmptyPost);
      } else if (!best) {
        const first = tiers[0];
        const needed = first ? Math.max(first.threshold - count, 0) : 0;
        const countTxt = count + ' ' + this.itemWord(count);
        const neededTxt = needed + ' ' + this.itemWord(needed);
        const lead = String(c.statusProgressLead || '').replace('[count]', countTxt);
        const rest = String(c.statusProgressRest || '')
          .replace('[needed]', neededTxt)
          .replace('[next]', first ? first.amount : '');
        html = '<strong>' + esc(lead) + '</strong> ' + esc(rest);
      } else {
        html = '<strong>' + this.wrapRed(esc(best.statusLead)) + '</strong> ' + esc(best.statusRest);
      }

      statusEl.innerHTML = html.replace(/\s+/g, ' ').trim();
    }

    updateBar(bestUnlockedEl) {
      if (!this.barEl) return;

      if (!bestUnlockedEl) {
        this.barEl.classList.remove('is-visible');
        this._lastBarThreshold = null;
        return;
      }

      const threshold = parseInt(bestUnlockedEl.dataset.tierThreshold, 10);
      if (this._lastBarThreshold === threshold && this.barEl.classList.contains('is-visible')) return;

      const isTop = bestUnlockedEl.dataset.tierIsTop === 'true';
      const amountEl = bestUnlockedEl.querySelector('[data-tier-amount]');
      const amountText = amountEl ? amountEl.textContent.trim() : '';
      const suffix = isTop ? this.config.bestOfferSuffix : this.config.regularUnlockSuffix;

      const titleEl = this.barEl.querySelector('[data-bar-title]');
      if (titleEl) titleEl.textContent = amountText + suffix;

      this.positionBar();
      requestAnimationFrame(() => this.barEl.classList.add('is-visible'));
      this._lastBarThreshold = threshold;
    }

    updateTote(eligibleCount) {
      const toteEl = this.activeBlock.querySelector('[data-tote-card]');
      if (!toteEl) return;
      const toteThreshold = parseInt(this.config.toteThreshold, 10) || 3;
      if (eligibleCount >= toteThreshold) toteEl.classList.add('is-visible');
      else toteEl.classList.remove('is-visible');
    }
  }

  function initPersonaResults() {
    document.querySelectorAll('.persona-result').forEach(section => {
      if (!section.__personaResultInitialized) {
        section.__personaResultInitialized = true;
        new PersonaResult(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPersonaResults);
  } else {
    initPersonaResults();
  }

  document.addEventListener('shopify:section:load', initPersonaResults);
})();
